import type { Command, CommandPlugin } from '@nora-monorepo/ui-shared';
import { i18n as i18nSetup, useDarkModeStore } from '@nora-monorepo/ui-shared';

import { router } from '#nora-ui/router';
import { navigateToRoute, navigateToWorkspace, toggleRoute } from '#nora-ui/router/navigation';
import { RouteName } from '#nora-ui/router/route-names';

const { t } = i18nSetup.global;

export function DefaultCommandsPlugin(): CommandPlugin {
  const id = 'defaultCommandsPlugin';

  async function getCommands(): Promise<Command[]> {
    const pluginCommands: Command[] = [];

    pluginCommands.push(
      {
        id: 'setDarkMode',
        title: t('commands.plugins.settings.setDarkMode'),
        action: () => {
          const darkModeStore = useDarkModeStore();
          darkModeStore.setDarkMode('dark');
        },
      },
      {
        id: 'setLightMode',
        title: t('commands.plugins.settings.setLightMode'),
        action: () => {
          const darkModeStore = useDarkModeStore();
          darkModeStore.setDarkMode('light');
        },
      },
      {
        id: 'setSystemMode',
        title: t('commands.plugins.settings.setSystemMode'),
        action: () => {
          const darkModeStore = useDarkModeStore();
          darkModeStore.setDarkMode('system');
        },
      },
      {
        id: 'toggleChat',
        title: t('commands.plugins.conversations.toggleConversationsTitle'),
        description: t(
          'commands.plugins.conversations.toggleConversationsDescription',
        ),
        keywords: t(
          'commands.plugins.conversations.toggleConversationsKeywords',
        ),
        action: () => {
          toggleRoute(RouteName.Chat);
        },
      },
      {
        id: 'toggleFiles',
        title: t('commands.plugins.files.toggleFilesTitle'),
        description: t('commands.plugins.files.toggleFilesDescription'),
        keywords: t('commands.plugins.files.toggleFilesKeywords'),
        action: () => {
          toggleRoute(RouteName.Files);
        },
      },
      {
        id: 'toggleGroups',
        title: t('commands.plugins.groups.toggleGroupsTitle'),
        description: t('commands.plugins.groups.toggleGroupsDescription'),
        keywords: t('commands.plugins.groups.toggleGroupsKeywords'),
        action: () => {
          toggleRoute(RouteName.Groups);
        },
      },
      {
        id: 'toggleOperations',
        title: t('commands.plugins.operations.toggleOperationsTitle'),
        description: t(
          'commands.plugins.operations.toggleOperationsDescription',
        ),
        keywords: t('commands.plugins.operations.toggleOperationsKeywords'),
        action: () => {
          toggleRoute(RouteName.Operations);
        },
      },
      {
        id: 'createOperation',
        title: t('commands.plugins.operations.createOperationTitle'),
        description: t(
          'commands.plugins.operations.createOperationDescription',
        ),
        keywords: t('commands.plugins.operations.createOperationKeywords'),
        action: () => {
          navigateToRoute(RouteName.Operations, undefined, { createOperation: 'true' });
        },
      },
      {
        id: 'toggleSettings',
        title: t('commands.plugins.settings.toggleSettingsTitle'),
        description: t('commands.plugins.settings.toggleSettingsDescription'),
        keywords: t('commands.plugins.settings.toggleSettingsKeywords'),
        action: () => {
          toggleRoute(RouteName.Settings);
        },
      },
      {
        id: 'toggleNotifications',
        title: t('commands.plugins.notifications.toggleNotificationsTitle'),
        description: t(
          'commands.plugins.notifications.toggleNotificationsDescription',
        ),
        keywords: t(
          'commands.plugins.notifications.toggleNotificationsKeywords',
        ),
        action: () => {
          toggleRoute(RouteName.Notifications);
        },
      },
      {
        id: 'closeAllModals',
        title: t('commands.plugins.windows.toggleCloseAllWindowsTitle'),
        description: t(
          'commands.plugins.windows.toggleCloseAllWindowsDescription',
        ),
        keywords: t('commands.plugins.windows.toggleCloseAllWindowsKeywords'),
        action: () => {
          if (router.currentRoute.value.path !== '/') {
            navigateToWorkspace();
          }
        },
      },
    );

    return pluginCommands;
  }

  return {
    id,
    getCommands,
  };
}
